<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <FormTitle>{{ t('whenToSell.title') }}</FormTitle>
      <FormContent>
        <Space size="8px" direction="vertical">
          <template v-for="item in getItems" :key="item.value">
            <FormRadioBoxItem
              :label="item.label"
              name="when"
              :value="item.value.toString()"
              :checked="Number(formData.whenToSell).toString()"
              @onClick="onItemClick"
            />
          </template>
        </Space>
        <ValuationFunnelErrorText v-if="isShowErrorMessage" />
      </FormContent>
    </TransitionWrapper>
  </FormContainer>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import {
  FormContainer,
  FormContent,
  FormHeader,
  FormRadioBoxItem,
  FormTitle,
  Space,
  TransitionWrapper,
  ValuationFunnelErrorText,
} from '@/components/common';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { WhenToSell } from '@/models/valuationFunnel';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { getStepInputs, isPropertyDetailDataPrefilled } from '@/tracking/tracking';
import { ValuationFlow } from '@/types';

const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  step: formStep,
  propertyDetailsData,
  selectedPropertyDetailIndex,
} = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const route = useRoute();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

const isShowErrorMessage = ref(false);

// Computed items (replacing data method)
const getItems = computed(() => [
  {
    label: t('whenToSell.asap'),
    value: WhenToSell.Now,
  },
  {
    label: t('whenToSell.nextSixMonths'),
    value: WhenToSell.Within6Month,
  },
  {
    label: t('whenToSell.nextTwoYears'),
    value: WhenToSell.Within2Year,
  },
  {
    label: t('whenToSell.overTwoYears'),
    value: WhenToSell.Later,
  },
]);

// Methods
const onItemClick = (value: string): void => {
  isShowErrorMessage.value = false;
  propertyValuationStore.setFormData({ whenToSell: parseInt(value) });
  onNext();
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.WhenToSell);
};

const onNext = (): void => {
  if (formData.value.whenToSell === undefined) {
    isShowErrorMessage.value = true;
    return;
  }
  propertyValuationStore.navigateNext(ValuationFlow.WhenToSell);
};

// Lifecycle (replacing mounted)
onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_whenToSell',
    locale: locale.value,
    stepInputs: getStepInputs(
      {
        ...formData.value,
        aptDetails: isPropertyDetailDataPrefilled(
          propertyDetailsData.value,
          selectedPropertyDetailIndex.value,
        ),
      },
      route.query.source?.toString() || '',
      formStep.value,
    ),
  });
});
</script>
